import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginImage, loginLogo } from "../../assets";
import LoginForm from "./components/LoginForm";
import EmailForm from "./components/EmailForm";
import PinCodeForm from "./components/PinCodeForm";
import ConfrimPassword from "./components/ConfrimPassword";
import { _Code_Verification, _Email_Verification } from "../../DAL/Login/Auth";
import { useSnackbar } from "notistack";
import { _Reset_Admin_Password } from "../../DAL/Login/ResetPassword";
import { SidebarLogo } from "../../assets";
import { Icon } from "@iconify/react/dist/iconify.js";

const Login = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [formState, setFormState] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // This function scrolls the page smoothly to an element with the id 'handle_move'.
  // 1. It first gets the DOM element using `document.getElementById("handle_move")`.
  // 2. If the element exists, it calls the `scrollIntoView` method on it to bring the element into view.
  // 3. The `scrollIntoView` method is set to scroll smoothly, and the block option is set to "start" to align the element at the top of the viewport.
  const handleMove = () => {
    const handle_move = document.getElementById("handle_move");
    if (handle_move) {
      handle_move.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // This function clears the error message when closing the alert.
  // 1. It sets the state `errorMessage` to an empty string, effectively hiding the error message.
  const handleAlertClose = () => {
    setErrorMessage("");
  };

  // This function handles the submission of the email for verification.
  // 1. It stores the provided email in local storage.
  // 2. Sets the loading state to true while awaiting the email verification process.
  // 3. Sends the email to the backend for verification using the `_Email_Verification` function.
  // 4. If the verification is successful, it shows a success message, updates the form state to `1`,
  //    and stores the state in local storage as `1` to indicate success.
  // 5. If the verification fails, it shows an error message, updates the form state to `0`,
  //    and stores the state in local storage as `0` to indicate failure.
  const handleSubmitEmail = async (_email) => {
    localStorage.setItem("email", _email);
    setIsLoading(true);
    const formData = {
      type: 0,
      email: _email,
    };

    const response = await _Email_Verification(formData);
    if (response.code === 200) {
      enqueueSnackbar(response.message, { variant: "success" });
      setIsLoading(false);
      localStorage.setItem("state", 1);
      setFormState(1);
    } else {
      localStorage.setItem("state", 0);
      setFormState(0);
      enqueueSnackbar(response.message, { variant: "error" });
      setIsLoading(false);
    }
  };

  // This function handles the submission of the pin code for verification.
  // 1. It retrieves the stored email from local storage.
  // 2. Sets the loading state to true while awaiting the pin code verification process.
  // 3. Sends the email, pin code, and verification type to the backend using the `_Code_Verification` function.
  // 4. If the verification is successful, it shows a success message, updates the form state to `2`,
  //    and stores the state in local storage as `2` to indicate success.
  // 5. If the verification fails, it shows an error message, updates the form state to `1`,
  //    and stores the state in local storage as `1` to indicate failure.
  const handleSubmitPinCode = async (pinCode) => {
    const _email = localStorage.getItem("email");
    setIsLoading(true);
    const formData = {
      email: _email,
      type: 0,
      verification_code: pinCode,
    };
    const response = await _Code_Verification(formData);
    if (response.code === 200) {
      enqueueSnackbar(response.message, { variant: "success" });
      setIsLoading(false);
      localStorage.setItem("state", 2);
      setFormState(2);
    } else {
      localStorage.setItem("state", 1);
      setFormState(1);
      enqueueSnackbar(response.message, { variant: "error" });
      setIsLoading(false);
    }
  };

  // This function handles the submission of a new password for the admin reset process.
  // 1. It sets the loading state to true while awaiting the password reset request.
  // 2. It retrieves the stored email from local storage to use it in the password reset request.
  // 3. It sends the new password, confirm password, and email to the backend using the `_Reset_Admin_Password` function.
  // 4. If the password reset is successful, it shows a success message, updates the form state to `-1` (indicating completion),
  //    and stores the state in local storage as `-1`.
  // 5. If the password reset fails, it shows an error message, updates the form state to `2` (indicating failure),
  //    and stores the state in local storage as `2`.
  // 6. After the reset attempt (whether successful or not), the user is redirected to the login page.

  const handleSubmitNewPassword = async (newPassword, confirmPassword) => {
    setIsLoading(true);
    const _email = localStorage.getItem("email");
    const formData = {
      type: 0,
      email: _email,
      password: newPassword,
      confirm_password: confirmPassword,
    };

    const response = await _Reset_Admin_Password(formData);
    if (response.code === 200) {
      enqueueSnackbar(response.message, { variant: "success" });
      setIsLoading(false);
      localStorage.setItem("state", -1);
      setFormState(-1);
    } else {
      enqueueSnackbar(response.message, { variant: "error" });
      localStorage.setItem("state", 2);
      setFormState(2);
      setIsLoading(false);
    }
    navigate("/login");
  };

  useEffect(() => {
    const formState = localStorage.getItem("state");
    if (formState) {
      setFormState(parseInt(formState));
    }
  }, [formState]);

  return (
    <>
      <div className="full-height-container">
        <div className="row g-0">
          <div
            className="col-md-7 overlay-container d-none d-lg-block"
            style={{
              position: "relative",
              backgroundImage: `url(${loginImage})`,
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center center",
            }}
          >
            <div class="div-img-overlay"></div>
          </div>

          <div className="col-md-5 d-flex align-items-center" id="handle_move">
            <div className="p-5 py-0 card-body">
              <div className="d-flex flex-column mb-4">
                <div className="text-center ">
                  <img
                    src={loginLogo}
                    className="img-fluid image-width"
                    alt="Login Logo"
                  />
                </div>
              </div>

              <>
                {formState === 0 ? (
                  <EmailForm
                    handleSubmitEmail={handleSubmitEmail}
                    setErrorMessage={setErrorMessage}
                    handleMove={handleMove}
                    isLoading={isLoading}
                    setFormState={setFormState}
                  />
                ) : formState === 1 ? (
                  <PinCodeForm
                    setFormState={setFormState}
                    handleSubmitPinCode={handleSubmitPinCode}
                    isLoading={isLoading}
                    errorMessage={errorMessage}
                    handleAlertClose={handleAlertClose}
                    setErrorMessage={setErrorMessage}
                    handleMove={handleMove}
                  />
                ) : formState === 2 ? (
                  <ConfrimPassword
                    handleSubmitNewPassword={handleSubmitNewPassword}
                    isLoading={isLoading}
                    errorMessage={errorMessage}
                    handleAlertClose={handleAlertClose}
                    setErrorMessage={setErrorMessage}
                    handleMove={handleMove}
                  />
                ) : (
                  <LoginForm setFormState={setFormState} />
                )}
              </>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
